import { MessageChatCircle } from "@untitledui/icons";
import { useState } from "react";
import { useHomeSummary } from "@/api/home";
import { Button } from "@/components/base/buttons/button";
import { useCurrency } from "@/hooks/use-currency";
import type { ReportRequest } from "@/pages/ask/components/ReportPicker";
import { firstName, greeting } from "@/pages/ask/lib/greeting";
import { HERO_SUGGESTIONS } from "../lib/hero-content";
import { heroHeadline, heroHeadlineText } from "../lib/hero-headline";
import type { DistributionRange } from "../lib/ranges";
import { HeroReportPicker } from "./hero-report-picker";

const HEADLINE_DELAY_MS = 450;
const HEADLINE_STAGGER_MS = 220;

const PORTRAIT_MASK = "radial-gradient(ellipse 55% 60% at 60% 45%, black 55%, transparent 100%)";

type AskUnboxHeroProps = {
  userName?: string;
  userEmail?: string;
  onAsk: (question: string) => void;
  onGenerateReport: (report: ReportRequest) => void;
  brandName: string;
  range: DistributionRange;
  rangeReady: boolean;
};

export function AskUnboxHero({
  userName,
  userEmail,
  onAsk,
  onGenerateReport,
  brandName,
  range,
  rangeReady,
}: AskUnboxHeroProps) {
  const [question, setQuestion] = useState("");
  const name = firstName(userName, userEmail);

  const { data: summary } = useHomeSummary({ from: range.from, to: range.to }, { enabled: rangeReady });
  const currency = useCurrency();
  const headline = summary ? heroHeadline(summary, range, currency) : null;
  const headlineLines = headline
    ? [
        { key: "gmv", content: headline.gmv },
        headline.change && {
          key: "change",
          content: (
            <>
              <span className={headline.change.direction === "up" ? "text-[#75e0a7]" : "text-[#fda29b]"}>
                {headline.change.direction} {headline.change.percent}
              </span>{" "}
              ({headline.change.delta})
            </>
          ),
        },
        { key: "creators", content: headline.creators },
      ].filter((line) => !!line)
    : [];

  return (
    <div className="relative flex w-full shrink-0 flex-col items-start overflow-hidden bg-linear-to-b from-unbox-dark-brand-start to-unbox-dark-brand-end pt-24 pb-[100px] pl-28">
      <img
        aria-hidden="true"
        alt=""
        src="/home/hero-portrait.webp"
        style={{
          maskImage: PORTRAIT_MASK,
          WebkitMaskImage: PORTRAIT_MASK,
          animation: "portrait-fade-in 1.1s ease-out both, portrait-breathe 7s ease-in-out 1.1s infinite",
        }}
        className="pointer-events-none absolute top-[20px] right-[-30px] h-[560px] w-auto max-w-none object-cover"
      />

      <div className="relative z-10 flex flex-col gap-2">
        <p
          style={{ animation: "fade-in-up 0.6s ease-out both" }}
          className="text-2xl leading-[44px] font-normal tracking-[-0.48px] text-[#c0a7f2]"
        >
          {greeting(new Date())}
          {name ? `, ${name}` : ""}.
        </p>
        <h1 className="min-h-[88px] max-w-[760px] text-[36px] leading-[44px] font-medium tracking-[-0.72px] text-white">
          {/* The visible lines animate in one by one, so they are split across
              elements a screen reader would announce separately. */}
          {headline && (
            <>
              <span className="sr-only">{heroHeadlineText(headline)}</span>
              <span key={`${range.from}-${range.to}`} aria-hidden="true">
                {headlineLines.map((line, index) => (
                  <span
                    key={line.key}
                    className="block"
                    style={{
                      animation: `fade-in-up 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${HEADLINE_DELAY_MS + index * HEADLINE_STAGGER_MS}ms both`,
                    }}
                  >
                    {line.content}
                  </span>
                ))}
              </span>
            </>
          )}
        </h1>
      </div>

      <div
        style={{ animation: "fade-in-up 0.55s ease-out 0.18s both" }}
        className="relative z-10 mt-16 flex h-14 w-[820px] max-w-full items-center overflow-hidden rounded-xl border border-[#5e5e5e] bg-linear-to-r from-[rgba(128,123,132,0.3)] to-[rgba(216,173,253,0.3)] pl-5 transition-shadow duration-200 focus-within:border-[#8c7a9e] focus-within:shadow-[0_0_0_4px_rgba(196,181,253,0.15)]"
      >
        <input
          type="text"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && question.trim()) onAsk(question.trim());
          }}
          placeholder="Ask Unbox anything…"
          aria-label="Ask Unbox anything"
          className="flex-1 bg-transparent text-base text-[#fffaeb] outline-none placeholder:text-[#fffaeb]"
        />
        <HeroReportPicker brandName={brandName} onGenerate={onGenerateReport} />
        <Button
          color="secondary"
          className="h-full! shrink-0 self-stretch rounded-l-none! rounded-r-xl! px-6 py-0!"
          iconLeading={<MessageChatCircle width={18} height={18} />}
          onClick={() => onAsk(question.trim())}
        >
          Ask Unbox
        </Button>
      </div>

      <div
        style={{ animation: "fade-in-up 0.55s ease-out 0.3s both" }}
        className="relative z-10 mt-5 grid w-[820px] max-w-full grid-cols-2 gap-x-6 gap-y-2"
      >
        {HERO_SUGGESTIONS.map(({ icon: Icon, question: suggestion }) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => onAsk(suggestion)}
            className="flex items-center gap-1.5 py-1 text-left text-sm text-white/70 transition-colors duration-150 hover:text-white"
          >
            <Icon width={15} height={15} className="shrink-0 text-[#c0a7f2]" />
            <span className="underline decoration-dotted decoration-white/40 underline-offset-4">{suggestion}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
